/**
 * ERP Financial API client for segment groups search.
 * @see https://erp.fms.ogintegration.us/docs/api/segment-groups#search-groups
 */

import { baseErpRequest } from './erpRequest';
import { ErpApiError } from './types';

export type SegmentGroupType = 'LEAF' | 'PARENT';

export type SegmentGroupSearchGroupTypeParam = 'All' | 'Parent' | 'Leaf';

export type ErpSegmentGroup = {
  id: number;
  entityId: number;
  segmentId: number;
  name: string;
  displayNameOnReport: string;
  type: SegmentGroupType;
  active: boolean;
  createdBy: string;
  updatedBy: string;
  createdDate: string;
  updatedDate: string;
  code: string | null;
  defaultParent: number | null;
  description: string | null;
  purpose: string | null;
  tags: string[];
  tagsAsString: string | null;
  importFileId: number | null;
  segmentName?: string;
  hasHierarchy?: boolean;
  order?: number;
  reporting?: string;
  draft?: boolean;
  archived?: boolean;
  default?: boolean;
};

export type ErpSegmentGroupsPageInfo = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  startCursor?: string;
  endCursor?: string;
  currentPage?: number;
  totalPages?: number;
};

/** @deprecated Legacy filter shape */
export type SegmentGroupFilter = {
  key: string;
  operation: string;
  value: string;
};

/** AG Grid text filter — matches ERP manage-segment-groups search UI */
export type SegmentGroupAgGridTextFilter = {
  filterType: 'text';
  type: 'contains' | 'equals';
  filter: string;
};

export type SegmentGroupSearchFilters = Record<string, SegmentGroupAgGridTextFilter>;

export type SegmentGroupSearchSort = {
  field: string;
  direction: 'asc' | 'desc';
};

/**
 * Pagination for search. Two mutually exclusive modes: `cursor` walks pages, and `pageNumber`
 * addresses one offset page — used when a caller needs every group in a single response (e.g.
 * page 0 + pageSize 1000). ERP behaviour is undefined if both are sent, so
 * {@link BuildSegmentGroupSearchBodyParams} keeps them apart at the type level.
 */
export type SegmentGroupSearchPagination = {
  pageSize: number;
  cursor?: string | null;
  pageNumber?: number;
  sorts?: SegmentGroupSearchSort[];
};

export type SegmentGroupSearchRequest = {
  pagination: SegmentGroupSearchPagination;
  filters?: SegmentGroupSearchFilters;
};

export const SEGMENT_GROUPS_PAGE_SIZE = 25;

// Root-level groups in the report builder are sorted by name ascending.
const DEFAULT_SORTS: SegmentGroupSearchSort[] = [{ field: 'name', direction: 'asc' }];

type SegmentGroupSearchBodyBaseParams = {
  pageSize?: number;
  searchQuery?: string;
};

/**
 * Cursor and offset paging are exclusive: a caller passes `cursor` to walk pages, or `pageNumber`
 * to address one offset page, never both.
 */
export type BuildSegmentGroupSearchBodyParams = SegmentGroupSearchBodyBaseParams &
  (
    | { cursor?: string; pageNumber?: never }
    | {
        /** Offset paging, for callers that need every group in one response instead of a cursor walk. */
        pageNumber: number;
        cursor?: never;
      }
  );

/** Builds cursor-paged body for ERP POST /segments/{id}/groups/search */
export function buildSegmentGroupSearchBody({
  pageSize = SEGMENT_GROUPS_PAGE_SIZE,
  cursor,
  searchQuery,
  pageNumber
}: BuildSegmentGroupSearchBodyParams = {}): SegmentGroupSearchRequest {
  const pagination: SegmentGroupSearchPagination = {
    pageSize,
    sorts: DEFAULT_SORTS
  };
  if (cursor) {
    pagination.cursor = cursor;
  }
  if (pageNumber !== undefined) {
    pagination.pageNumber = pageNumber;
  }

  const trimmedSearch = searchQuery?.trim();
  if (!trimmedSearch) {
    return { pagination };
  }

  return {
    pagination,
    filters: {
      name: { filterType: 'text', type: 'contains', filter: trimmedSearch }
    }
  };
}

export type ErpSegmentGroupsResponse = {
  message: string;
  statusCode: number;
  data: {
    data: ErpSegmentGroup[];
    pageInfo: ErpSegmentGroupsPageInfo;
  };
  errors?: ErpApiError[] | null;
};

export type SearchSegmentGroupsParams = {
  segmentId: number;
  groupType?: SegmentGroupSearchGroupTypeParam;
  body: SegmentGroupSearchRequest;
};

export const searchSegmentGroups = async (
  params: SearchSegmentGroupsParams,
  clientOptions: RequestInit = {},
  hostUrl?: string
): Promise<ErpSegmentGroupsResponse> => {
  const { segmentId, groupType, body } = params;
  const req = baseErpRequest(`/segments/${encodeURIComponent(segmentId)}/groups/search`, clientOptions, hostUrl);
  const withQuery = groupType ? req.query({ groupType }) : req;
  return withQuery.json(body).post().json();
};

export type GetSegmentGroupByIdParams = {
  segmentId: number;
  groupId: number;
};

export type ErpSegmentGroupResponse = {
  message: string;
  statusCode: number;
  data: ErpSegmentGroup;
  errors?: ErpApiError[] | null;
};

export const getSegmentGroupById = async (
  params: GetSegmentGroupByIdParams,
  clientOptions: RequestInit = {},
  hostUrl?: string
): Promise<ErpSegmentGroupResponse> => {
  const { segmentId, groupId } = params;
  return (await baseErpRequest(
    `/segments/${encodeURIComponent(segmentId)}/groups/${encodeURIComponent(groupId)}`,
    clientOptions,
    hostUrl
  ).get().json()) as ErpSegmentGroupResponse;
};

export type DefaultSegmentGroup = {
  id: number;
  name: string;
};

export type DefaultSegmentGroupsResponse = {
  data: DefaultSegmentGroup[];
  message: string;
  statusCode: number;
};

export const getDefaultSegmentGroups = async (
  params: { segmentId: number; groupType: SegmentGroupType },
  clientOptions: RequestInit = {},
  hostUrl?: string
): Promise<DefaultSegmentGroupsResponse> => {
  const { segmentId, groupType } = params;
  return (await baseErpRequest(
    `/segments/${encodeURIComponent(segmentId)}/groups/default`,
    clientOptions,
    hostUrl
  ).query({ groupType }).get().json()) as DefaultSegmentGroupsResponse;
};
